import { ChainID, Timeframe } from '@ankr.com/chains-list';
import { t } from '@ankr.com/common';
import { useMemo } from 'react';

import { StatsTimeframe } from 'modules/stats/types';
import { formatLongNumber } from 'modules/common/utils/formatLongNumber';
import { selectChainById } from 'modules/chains/store/selectors';
import { selectPrivateBlockchainCountByChainID } from 'modules/stats/store/private/selectors';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAppSelector } from 'store/useAppSelector';
import { useIsXSDown } from 'uiKit/Theme/useTheme';
import { useProjectStatsParams } from 'modules/projects/components/ProjectStatsProvider';

export const useChainRequests = (chainId: ChainID, timeframe: Timeframe) => {
  const oneHourStatsParams = useProjectStatsParams({
    timeframe: StatsTimeframe.Hour,
  });

  const lastHourRequests = useAppSelector(state =>
    selectPrivateBlockchainCountByChainID(state, oneHourStatsParams, chainId),
  );

  const oneDayStatsParams = useProjectStatsParams({
    timeframe: StatsTimeframe.Day,
  });

  const lastDayRequests = useAppSelector(state =>
    selectPrivateBlockchainCountByChainID(state, oneDayStatsParams, chainId),
  );

  const { isPremium } = useAccountStatus();

  const isPremiumChain = useAppSelector(state =>
    selectChainById(state, chainId),
  )?.premiumOnly;

  const isMobile = useIsXSDown();

  const requestsString = useMemo(() => {
    if (!isPremium && isPremiumChain) {
      return isMobile
        ? t('projects.list-project.not-available')
        : t('projects.list-project.not-available-on-freemium');
    }

    const hasLastHourRequests = lastHourRequests > 0;

    if (timeframe === Timeframe.Hour && hasLastHourRequests) {
      return formatLongNumber(lastHourRequests);
    }

    const hasLastDayRequests = lastDayRequests > 0;

    if (timeframe === Timeframe.Day && hasLastDayRequests) {
      return formatLongNumber(lastDayRequests);
    }

    return t('projects.list-project.no-requests-yet');
  }, [
    isMobile,
    isPremium,
    isPremiumChain,
    lastDayRequests,
    lastHourRequests,
    timeframe,
  ]);

  const hasRequestsData =
    (timeframe === Timeframe.Hour && lastHourRequests > 0) ||
    (timeframe === Timeframe.Day && lastDayRequests > 0);

  return { requestsString, hasRequestsData };
};
